/**
 * @file LoadAllButtonView.tsx
 * @description Presentation layer for the "Load all N" / "Show less" toggle
 * button shown beneath truncated player tier lists.
 * @module features/squad-planning/components/LoadAllButton
 */

import { memo } from "react";

import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/** Props for {@link LoadAllButtonView} */
export interface LoadAllButtonViewProps {
  /** Total number of players in the tier */
  readonly totalCount: number;
  /** Whether the tier is currently showing all players */
  readonly isExpanded: boolean;
  /** Callback to toggle between truncated and full list */
  readonly onPress: () => void;
}

/**
 * Renders a full-width button that toggles between "Load all N" and "Show less".
 * @param props - View props
 * @param props.totalCount - Total player count displayed in the label
 * @param props.isExpanded - Whether the list is fully expanded
 * @param props.onPress - Toggle callback bound to the tier name by the container
 * @returns A pressable text button
 */
const LoadAllButtonView = ({
  totalCount,
  isExpanded,
  onPress,
}: LoadAllButtonViewProps) => (
  <Pressable testID="load-all-button" onPress={onPress} className="px-4 py-2">
    <Text variant="micro-strong" className="text-accent-primary">
      {isExpanded ? "\u2190 Show less" : `Load all ${totalCount} \u2192`}
    </Text>
  </Pressable>
);

LoadAllButtonView.displayName = "LoadAllButtonView";

export default memo(LoadAllButtonView);
